// Chat provider + keys. BYOK, browser-only, localStorage, namespaced
// per Google subject id.
//
// Two providers:
//   babyai    — "Bundle of Babies," Christopher's MoE Space. The DEFAULT.
//               Zero-setup: a build-time HF token (VITE_BABYAI_HF_TOKEN)
//               makes it work out of the box; a pasted token overrides.
//   anthropic — Claude, BYOK. The alternative for creators who'd rather
//               bring their own frontier key.
//
// Distinct from lib/secrets.ts (per-creator infra tokens) — this is
// AI-provider config, kept separate so the chat UI doesn't reach into
// onboarding.

import { create } from "zustand";
import { DEFAULT_MODEL as DEFAULT_ANTHROPIC_MODEL } from "../lib/anthropic-client";
import { DEFAULT_BABYAI_MODEL, defaultBabyaiToken } from "../lib/babyai-client";

export type Provider = "babyai" | "anthropic";

const PROVIDER_STORAGE = "bka/chat_provider";
const ANTHROPIC_MODEL_STORAGE = "bka/anthropic_model";
const BABYAI_MODEL_STORAGE = "bka/babyai_model";
function anthropicKeyStorage(sub: string): string { return `bka/anthropic_key/${sub}`; }
function babyaiKeyStorage(sub: string): string { return `bka/babyai_key/${sub}`; }

function load(k: string): string | null {
  if (typeof localStorage === "undefined") return null;
  try { return localStorage.getItem(k); } catch { return null; }
}

interface KeyState {
  sub: string | null;
  provider: Provider;
  anthropic: string | null;          // BYOK Anthropic key
  babyai: string | null;             // HF token (pasted override of the baked default)
  anthropicModel: string;
  babyaiModel: string;

  hydrate: (googleSub: string) => void;
  setProvider: (p: Provider) => void;
  setAnthropicKey: (key: string) => void;
  clearAnthropicKey: () => void;
  setBabyaiKey: (key: string) => void;
  clearBabyaiKey: () => void;
  setModel: (provider: Provider, model: string) => void;

  /** Resolved token for the active provider (pasted > baked default). */
  activeToken: () => string | null;
  activeModel: () => string;
  /** True if the active provider can actually be called right now. */
  canChat: () => boolean;
}

export const useKeyStore = create<KeyState>((set, get) => ({
  sub: null,
  provider: (load(PROVIDER_STORAGE) as Provider) ?? "babyai",
  anthropic: null,
  babyai: null,
  anthropicModel: load(ANTHROPIC_MODEL_STORAGE) ?? DEFAULT_ANTHROPIC_MODEL,
  babyaiModel: load(BABYAI_MODEL_STORAGE) ?? DEFAULT_BABYAI_MODEL,

  hydrate: (googleSub) => {
    set({
      sub: googleSub,
      anthropic: load(anthropicKeyStorage(googleSub)),
      babyai: load(babyaiKeyStorage(googleSub)),
    });
  },

  setProvider: (p) => {
    if (typeof localStorage !== "undefined") localStorage.setItem(PROVIDER_STORAGE, p);
    set({ provider: p });
  },

  setAnthropicKey: (key) => {
    const sub = get().sub;
    if (sub && typeof localStorage !== "undefined") localStorage.setItem(anthropicKeyStorage(sub), key);
    set({ anthropic: key });
  },
  clearAnthropicKey: () => {
    const sub = get().sub;
    if (sub && typeof localStorage !== "undefined") localStorage.removeItem(anthropicKeyStorage(sub));
    set({ anthropic: null });
  },
  setBabyaiKey: (key) => {
    const sub = get().sub;
    if (sub && typeof localStorage !== "undefined") localStorage.setItem(babyaiKeyStorage(sub), key);
    set({ babyai: key });
  },
  clearBabyaiKey: () => {
    const sub = get().sub;
    if (sub && typeof localStorage !== "undefined") localStorage.removeItem(babyaiKeyStorage(sub));
    set({ babyai: null });
  },

  setModel: (provider, model) => {
    if (provider === "anthropic") {
      if (typeof localStorage !== "undefined") localStorage.setItem(ANTHROPIC_MODEL_STORAGE, model);
      set({ anthropicModel: model });
    } else {
      if (typeof localStorage !== "undefined") localStorage.setItem(BABYAI_MODEL_STORAGE, model);
      set({ babyaiModel: model });
    }
  },

  activeToken: () => {
    const s = get();
    return s.provider === "anthropic" ? s.anthropic : (s.babyai ?? defaultBabyaiToken());
  },
  activeModel: () => {
    const s = get();
    return s.provider === "anthropic" ? s.anthropicModel : s.babyaiModel;
  },
  canChat: () => !!get().activeToken(),
}));
